import React from "react";
import Header from "../components/Header";

export default function LogisticsManagementSystem() {
  return (
    <><Header />
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 p-4 md:p-6 font-sans">
      <div className="max-w-7xl mx-auto bg-white shadow-2xl p-6 md:p-8 rounded-2xl">
        {/* Enhanced Header */}
        <div className="flex items-center space-x-4 md:space-x-6">
          <div className="relative">
            <div className="w-12 h-24 bg-gradient-to-b from-blue-400 to-blue-700 rounded-xl shadow-lg"></div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-orange-500 rounded-full border-4 border-white"></div>
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-900 to-blue-600 bg-clip-text text-transparent">
              2-Logistics Management System
            </h1>
            <p className="text-sm md:text-base text-gray-600 mt-2">
              End-to-End Supply Chain & Distribution Management Platform
            </p>
          </div>
        </div>

        {/* Enhanced System Description */}
        <div className="mt-8 bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 rounded-2xl shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-800/20 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
              </svg>
              <h2 className="text-xl font-bold">System Description</h2>
            </div>
            <p className="text-base md:text-lg">
              Comprehensive management of the entire Urgent Distribution Logistics process, from order placement to final delivery, with real-time tracking and AI-powered optimization.
            </p>
          </div>
        </div>

        {/* Enhanced Process Steps */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
            </svg>
            End-to-End Process Flow
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            {[
              { label: "Order Awarding", icon: "📋" },
              { label: "Order Break-down", icon: "🔧" },
              { label: "Production", icon: "🏭" },
              { label: "Logistics Dispatch", icon: "🚚" },
              { label: "Site Reception", icon: "📦" }
            ].map((step, i) => (
              <div key={i} className="relative flex flex-col items-center">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center text-white text-2xl shadow-lg">
                  {step.icon}
                </div>
                <span className="mt-2 text-sm font-medium text-gray-700 text-center">{step.label}</span>
                {i < 4 && (
                  <div className="hidden md:block absolute top-7 left-full w-16 border-t-2 border-dashed border-blue-300"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced Description Points */}
        <div className="mt-10 p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-gray-200">
          <h3 className="text-xl font-bold text-blue-900 mb-6 flex items-center gap-3">
            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
            </svg>
            System Capabilities
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Real-time Data Recording</h4>
              <p className="text-sm text-gray-600">
                Automatic process initiation with real-time recording of all business data generated during intermediate stages.
              </p>
            </div>
            
            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Traceable Data Chain</h4>
              <p className="text-sm text-gray-600">
                Establishing correlations between personnel, organizations, and roles, forming a fully traceable end-to-end data chain.
              </p>
            </div>
            
            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Multi-dimensional Analytics</h4>
              <p className="text-sm text-gray-600">
                Creating comprehensive data consolidation and analysis to provide actionable insights for decision-makers.
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Features Section */}
        <div className="mt-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-6 h-24 bg-gradient-to-b from-orange-500 to-red-500 rounded-lg"></div>
            <h2 className="text-2xl font-bold text-gray-800">Key Features</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Feature Cards */}
            <div className="space-y-6">
              {[
                {
                  title: "Data Integration",
                  color: "bg-gradient-to-r from-blue-600 to-blue-800",
                  description: "Purchase order data integration, reducing data redundancy and achieving single-source management of purchase data.",
                  icon: "📊"
                },
                {
                  title: "Plan Tracking",
                  color: "bg-gradient-to-r from-orange-500 to-red-500",
                  description: "Break-down of production plans by levels, enabling item-by-item tracking with real-time updates.",
                  icon: "🎯"
                },
                {
                  title: "Online Collaboration",
                  color: "bg-gradient-to-r from-blue-600 to-blue-800",
                  description: "Initiate online meetings with automatic minutes generation and task assignment.",
                  icon: "👥"
                },
                {
                  title: "Overdue Reminding",
                  color: "bg-gradient-to-r from-orange-500 to-red-500",
                  description: "APP message reminders for nodes that may impact delivery time with escalation protocols.",
                  icon: "⏰"
                }
              ].map((feature, i) => (
                <div key={i} className="group cursor-pointer">
                  <div className={`${feature.color} text-white p-5 rounded-2xl shadow-lg transform transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-xl`}>
                    <div className="flex items-center gap-4">
                      <div className="text-2xl">{feature.icon}</div>
                      <h3 className="text-lg font-bold">{feature.title}</h3>
                    </div>
                    <p className="mt-3 text-sm opacity-90">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Side - Images */}
            <div className="space-y-6">
              <div className="rounded-2xl shadow-xl overflow-hidden bg-gradient-to-br from-blue-50 to-white border border-blue-100">
                <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white py-3 px-6 font-bold">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd" />
                    </svg>
                    Logistics Dashboard
                  </div>
                </div>
                <div className="p-4">
                  <img
                    src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                    alt="Logistics Management Dashboard"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                  <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded-lg">
                    Real-time tracking of shipments, inventory levels, and delivery status across all distribution channels.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl shadow-xl overflow-hidden bg-gradient-to-br from-blue-50 to-white border border-blue-100">
                <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white py-3 px-6 font-bold">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                    Workflow Visualization
                  </div>
                </div>
                <div className="p-4">
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                    alt="Logistics Workflow Diagram"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                  <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded-lg">
                    Visual representation of the complete logistics workflow from order to delivery with real-time status updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-12 p-6 bg-gradient-to-r from-blue-50 to-gray-50 rounded-2xl border border-blue-100">
          <h3 className="text-xl font-bold text-blue-900 mb-6 text-center">System Performance Metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-700">98%</div>
              <div className="text-sm text-gray-600 mt-2">On-time Delivery</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-700">45%</div>
              <div className="text-sm text-gray-600 mt-2">Cost Reduction</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-700">99.9%</div>
              <div className="text-sm text-gray-600 mt-2">Data Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-700">24/7</div>
              <div className="text-sm text-gray-600 mt-2">System Uptime</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}